import React, { useState, useEffect } from 'react';
import { Modal } from '../../../Smart/Modal/Modal';
import { DatePicker } from '../../../Pickers/DatePicker/DatePicker';
import { Button } from '../../../Form/Button/Button';
import { buttonColors } from '../../../Pickers/InputTheme';
import { i18n } from '../../../../Utils/i18n/i18n';
import { SignDocCategories } from '../../../../Utils/Constants';
import { get } from 'lodash';
import './ApproveUploadModal.scss';

/**
 * Modal for approving user uploaded documents
 * For CERTIFICATION category, requires manual expiry date input
 * @param {boolean} open - Whether modal is open
 * @param {Function} onClose - Close modal callback
 * @param {Object} userSignDoc - UserSignDoc being approved
 * @param {Function} onApprove - Approve callback with (userSignDocId, expiryDate)
 */
export const ApproveUploadModal = ({ open, onClose, userSignDoc, onApprove }) => {
    const [expiryDate, setExpiryDate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const isCertification = get(userSignDoc, 'signDoc.category') === SignDocCategories.CERTIFICATION;

    // Update expiryDate when userSignDoc changes or modal opens
    useEffect(() => {
        if (userSignDoc && open) {
            const existingExpiryDate = get(userSignDoc, 'expiryDate');
            if (existingExpiryDate) {
                setExpiryDate(new Date(existingExpiryDate));
            } else {
                setExpiryDate(null); // Force user to choose a date
            }
        }
    }, [userSignDoc, open]);

    const handleApprove = async () => {
        if (!userSignDoc) return;

        // Validate expiry date for CERTIFICATION
        if (isCertification) {
            if (!expiryDate) {
                setError(i18n.translate('pendingApprovals.approveDialog.expiryDateRequired'));
                return;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (expiryDate < today) {
                setError(i18n.translate('pendingApprovals.approveDialog.expiryDatePast'));
                return;
            }
        }

        setIsSubmitting(true);
        setError('');

        try {
            await onApprove(
                userSignDoc.id,
                isCertification ? expiryDate.toISOString() : null
            );
            // Reset state after successful approval
            setExpiryDate(null);
        } catch (error) {
            console.error('Error in ApproveUploadModal:', error);
            setError(i18n.translate('pendingApprovals.errors.approveFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setExpiryDate(null);
            setError('');
            onClose();
        }
    };

    const userName = get(userSignDoc, 'user')
        ? `${get(userSignDoc, 'user.firstName')} ${get(userSignDoc, 'user.lastName')}`
        : '';
    const signDocName = get(userSignDoc, 'signDoc.docName', '');

    return (
        <Modal open={open} onClose={handleClose}>
            <div className="approveUploadModal">
                <h2 className="approveUploadModal__title">
                    {i18n.translate('pendingApprovals.approveDialog.title')}
                </h2>

                <div className="approveUploadModal__content">
                    <div className="approveUploadModal__info">
                        <div className="approveUploadModal__infoRow">
                            <strong>{i18n.translate('pendingApprovals.approveDialog.user')}:</strong> {userName}
                        </div>
                        <div className="approveUploadModal__infoRow">
                            <strong>{i18n.translate('pendingApprovals.approveDialog.signDoc')}:</strong> {signDocName}
                        </div>
                    </div>

                    {isCertification && (
                        <>
                            <div className="approveUploadModal__description">
                                {i18n.translate('pendingApprovals.approveDialog.expiryDescription')}
                            </div>
                            <div className="approveUploadModal__datePicker">
                                <DatePicker
                                    onChange={(date) => {
                                        setExpiryDate(date);
                                        setError('');
                                    }}
                                    value={expiryDate}
                                    shouldDisableDate={(date) => {
                                        if (!date) return false;
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const checkDate = new Date(date);
                                        checkDate.setHours(0, 0, 0, 0);
                                        return checkDate < today;
                                    }}
                                    disablePast={true}
                                />
                            </div>
                            {error && (
                                <div className="approveUploadModal__error">
                                    {error}
                                </div>
                            )}
                        </>
                    )}

                    {!isCertification && (
                        <div className="approveUploadModal__description">
                            {i18n.translate('pendingApprovals.approveDialog.confirmMessage')}
                        </div>
                    )}
                </div>

                <div className="approveUploadModal__actions">
                    <Button
                        onClick={handleClose}
                        color={buttonColors.YELLOW}
                        disabled={isSubmitting}
                        text={i18n.translate('pendingApprovals.approveDialog.cancel')}
                    >
                    </Button>
                    <Button
                        onClick={handleApprove}
                        color={buttonColors.GREEN}
                        disabled={isSubmitting}
                        text={i18n.translate('pendingApprovals.approveDialog.confirm')}
                    >
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ApproveUploadModal;
